/**
 * Footage spec provenance — SOURCE OF TRUTH. Pure TypeScript, no Drizzle/DB
 * imports.
 *
 * These two enums exist to keep the system HONEST about where a piece of
 * footage-matching metadata came from. The b-roll matcher gates on them: a spec
 * that was only guessed from a service name must not unlock the same
 * exact-match procedure clips as one a clinician actually confirmed.
 *
 * See docs/plans/stock-footage-matching-architecture.md.
 */
/** Where a SERVICE's footage spec (agent, regions) came from. */
export const serviceSpecSourceLabels = {
    /**
     * A person set it: today a Borradh admin, from the clinic's admin page. The
     * only fully trusted state, so the name classifier never overwrites it.
     */
    declared: 'Set by a person',
    /**
     * Derived from the service name by the alias classifier. Good enough for
     * technique-grade matching; not enough to assert which specific machine a
     * clinic owns when several share a technique.
     */
    inferred_from_name: 'Inferred from name',
    /**
     * Nothing is known. The service name carries no treatment information
     * ("Chin", "Extra Large", "Body Contouring"). Procedure clips are refused;
     * the matcher falls back to ambient footage and reports a footage gap.
     */
    unknown: 'Not specified',
};
export const serviceSpecSourceValues = Object.keys(serviceSpecSourceLabels);
/** Where a CLIP's declared agent came from. */
export const clipAgentSourceLabels = {
    /**
     * A human curator labelled it when the clip entered the bank. Stock footage
     * is always this — it is labelled once, by whoever filmed or selected it,
     * and never inferred.
     */
    curated: 'Curated',
    /**
     * Inherited from the service this upload was linked to. Reliable when the
     * org has exactly one agent for the observed technique — a clip at a clinic
     * that only owns an Endospheres machine is Endospheres footage.
     */
    inherited: 'Inherited from service',
    /**
     * Identity unknown. Either nothing was linked, or the org owns several
     * machines of the same technique and the ambiguity was NOT resolved by
     * guessing. Treated as unknown by the gate — never as fact.
     */
    unconfirmed: 'Unconfirmed',
};
export const clipAgentSourceValues = Object.keys(clipAgentSourceLabels);
/**
 * How a stock clip entered the bank. Internal only — deliberately NOT a labels
 * record, because this is never surfaced in the product. No display strings
 * exist for it and none should be added.
 *
 * It used to decide whether a clip could name its agent at all, because a
 * MACHINE BRAND is not visible in footage someone else shot. Brands are no
 * longer offered (treatment_agent.is_active), and the product types that
 * remain (dermal filler, anti-wrinkle, Hydrafacial) are visible, so any
 * confirmed clip may name one.
 */
export const stockClipSourceValues = [
    /** Filmed by us. Whoever curated it was present and knows the machine. */
    'shot',
    /** Came from an external library. Deliberately does not record which one. */
    'provider',
    /**
     * Contributed by a clinic from its own uploads under a sharing grant. The
     * contributing clinic owns the machine, so it can declare the agent.
     */
    'pooled',
    /**
     * Generated by us from anonymised seed footage. Generated and provider
     * clips share the same matching gates. Own-shoot / pooled footage has no
     * intake path yet. The value
     * already exists in production's enum (added out of band in September with
     * the first generated batch); the migration adds it IF NOT EXISTS.
     */
    'generated',
];
/**
 * Sources whose clips may be matched to a clinic's service. One list, read by
 * the matcher, render-time readers, minting and admin pickers. Every source
 * still needs usable footage and matching treatment and area enums.
 */
export const matchableStockClipSources = [
    'generated',
    'provider',
];
/** Legacy allowlist retained for callers that apply their own origin policy.
 * The database no longer restricts agent tags by source; provenance alone is
 * not proof of product or machine identity. The new matcher does not use it. */
export const agentDeclarableSources = [
    'shot',
    'pooled',
];
/** How tightly a clip is framed. Observable, so safe to infer from pixels. */
export const clipFramingLabels = {
    /** Instrument and skin fill the frame. The default for face regions. */
    close_up: 'Close-up',
    /** Subject and immediate surroundings. The default for body regions. */
    mid: 'Mid-shot',
    /** Room or full scene — establishing shots, mostly ambient. */
    wide: 'Wide',
};
export const clipFramingValues = Object.keys(clipFramingLabels);
/**
 * Body regions — the second gate axis, and as load-bearing as the technique.
 *
 * Eyebrow filler and lip filler are the same syringe and completely different
 * shots; laser hair removal splits into ~40 body-part variants across 20 orgs.
 * A matcher that gates only on the technique will happily put a tear-trough
 * clip on a jawline-filler video.
 *
 * THE ONE LIST. The service classifier, the clip classifier, the matcher and
 * the CHECK constraints on `stock_clip`, `organization_service` and `asset` all
 * read it. Before this there were three lists that disagreed — an earlier,
 * snake_case version of this record that nothing imported, the service
 * classifier's own constant, and free text on clips — and the clip side
 * drifted (`face`, `arm`, `cheek`) into values that matched nothing, ever.
 *
 * The keys are the strings already stored in production (every classified
 * service uses them), so adopting the list needed no data migration on the
 * service side. `nose` and `bikini` are the only additions.
 *
 * CLOSED vocabulary, unlike `treatment_agent` — the human body does not gain
 * new regions, so adding one is a code change plus a migration, on purpose.
 *
 * See docs/plans/stock-footage-tagging.md.
 */
export const bodyRegionLabels = {
    forehead: 'Forehead',
    brow: 'Brow',
    eyes: 'Eyes',
    nose: 'Nose',
    lips: 'Lips',
    cheeks: 'Cheeks',
    chin: 'Chin',
    jaw: 'Jaw',
    'full face': 'Full face',
    neck: 'Neck',
    scalp: 'Scalp',
    hair: 'Hair',
    chest: 'Chest',
    abdomen: 'Abdomen',
    flank: 'Flank',
    back: 'Back',
    buttocks: 'Buttocks',
    arms: 'Arms',
    underarm: 'Underarm',
    hands: 'Hands',
    thighs: 'Thighs',
    legs: 'Legs',
    feet: 'Feet',
    teeth: 'Teeth',
    /**
     * Bikini line, Brazilian, Hollywood, peri-anal, vajacial and intimate
     * rejuvenation — 17 services across 5 orgs in the 2026-07-29 audit.
     *
     * The matcher NEVER serves a procedure clip for this region, at any grade,
     * regardless of how well-stocked the bank is. Such footage cannot appear in
     * a marketing video, so these services route to ambient by rule. This is a
     * product decision encoded in the vocabulary, not a coverage gap to fill.
     */
    bikini: 'Bikini & intimate',
    'body-general': 'Body (general)',
};
export const bodyRegionValues = Object.keys(bodyRegionLabels);
export const isBodyRegion = (value) => Object.hasOwn(bodyRegionLabels, value);
/**
 * Which broader region each region sits inside, for the matcher's covering
 * rule. A clip tagged with a region can serve a service asking for anything
 * that region covers — a full-face laser shot shows the chin — but NEVER the
 * reverse: a chin close-up is not a full-face treatment (ENG-669). That
 * one-way rule is the same trap as the `energy_contact` parent reaching every
 * child technique (#699); get the direction wrong and it fails quietly.
 *
 * Deliberately absent, because a broader shot does not actually show them:
 * `neck` (not in a full-face frame), `feet` (not in a legs shot), `hands` and
 * `underarm` (not in an arms shot), `scalp`, `hair`, `teeth`.
 */
export const bodyRegionParents = {
    forehead: 'full face',
    brow: 'full face',
    eyes: 'full face',
    nose: 'full face',
    lips: 'full face',
    cheeks: 'full face',
    chin: 'full face',
    jaw: 'full face',
    chest: 'body-general',
    abdomen: 'body-general',
    flank: 'body-general',
    back: 'body-general',
    buttocks: 'body-general',
    arms: 'body-general',
    legs: 'body-general',
    thighs: 'legs',
};
/**
 * Regions that must never receive a procedure clip. Read by the matcher gate;
 * kept as data rather than an `if` so the rule is visible next to the
 * vocabulary it constrains.
 */
export const NON_FILMABLE_REGIONS = ['bikini'];
/**
 * The clip regions that may illustrate a service declaring `serviceRegions`:
 * each region plus every region that covers it, transitively. Off-list and
 * non-filmable values contribute nothing.
 *
 * Returns an empty array when nothing filmable is declared. Callers must treat
 * an EMPTY service list as region-neutral BEFORE calling this, and must check
 * `hasOnlyNonFilmableRegions` separately — an empty result here means
 * different things in those two cases.
 */
export function regionsThatCanServe(serviceRegions) {
    const out = new Set();
    for (const raw of serviceRegions) {
        if (!isBodyRegion(raw) || NON_FILMABLE_REGIONS.includes(raw))
            continue;
        let region = raw;
        while (region && !out.has(region)) {
            out.add(region);
            region = bodyRegionParents[region];
        }
    }
    return [...out];
}
/**
 * True when a service declares regions and every one of them is non-filmable
 * ("Brazilian wax"). Such a service gets no procedure clip at any grade. A
 * service that also declares a filmable region ("full body wax": bikini, legs,
 * underarm) matches on the filmable ones.
 */
export function hasOnlyNonFilmableRegions(serviceRegions) {
    return (serviceRegions.length > 0 &&
        serviceRegions.every((r) => isBodyRegion(r) && NON_FILMABLE_REGIONS.includes(r)));
}
/**
 * Who decided a stock clip's tags. The AI's tags go live straight away
 * (`pending`); a person can check them (`confirmed`) or take the clip out of
 * every list (`rejected`). Only rejected clips are never used.
 * See docs/plans/stock-footage-tagging.md.
 */
export const stockClipTagStatusLabels = {
    pending: 'Set by AI',
    confirmed: 'Checked',
    rejected: 'Rejected',
};
export const stockClipTagStatusValues = Object.keys(stockClipTagStatusLabels);
/**
 * How a clinic's service gets its stock footage. `automatic`: the matcher's
 * picks for the service's treatment. `chosen`: clips a Borradh admin assigned
 * by hand, which replace the matcher's picks until they are cleared.
 */
export const serviceStockClipModeLabels = {
    automatic: 'Automatic',
    chosen: 'Chosen by hand',
};
export const serviceStockClipModeValues = Object.keys(serviceStockClipModeLabels);
/** The most stock clips an admin can choose for one service. */
export const MAX_CHOSEN_STOCK_CLIPS = 20;
/** Why the picture and title suggestions disagree. Shown to the reviewer. */
export const tagDisagreementLabels = {
    no_title: 'No contributor title to compare against',
    title_names_no_treatment: 'The title names no treatment, but the picture suggests one',
    treatment_differs: 'The picture and the title suggest different treatments',
    regions_conflict: 'The picture and the title suggest different body areas',
    product_differs: 'The picture and the title suggest different products',
};
export const tagDisagreementValues = Object.keys(tagDisagreementLabels);
/** Visible actions, shared by service requirements and reviewed footage. Unknown is []. */
export const serviceFootageOtherFieldValues = [
    'treatment',
    'product',
    'body_area',
    'action',
];
export const footageActionLabels = {
    injection: 'Injection',
    microneedling: 'Microneedling',
    device_contact: 'Device in contact with skin',
    laser_ipl: 'Laser or IPL application',
    topical_application: 'Topical application',
    cleansing: 'Cleansing',
    massage: 'Massage',
    extraction: 'Extraction',
    exfoliation: 'Exfoliation',
    hair_removal: 'Hair removal',
    cutting_styling: 'Cutting or styling',
    consultation: 'Consultation',
    assessment: 'Assessment',
    aftercare: 'Aftercare',
    result: 'Treatment result',
};
export const footageActionValues = Object.keys(footageActionLabels);
export const uploadFootageReviewStatusLabels = {
    pending: 'Needs review',
    confirmed: 'Confirmed',
    no_service: 'No service shown',
    needs_clinic: 'Needs clinic confirmation',
};
export const uploadFootageReviewStatusValues = Object.keys(uploadFootageReviewStatusLabels);
/** A service's confirmed area scope; unknown is never a wildcard. */
export const treatmentAreaStatusValues = [
    'unknown',
    'selected',
    'not_applicable',
];
export const stockSupportingStepValues = [
    'brow_grooming',
    'gentle_massage',
    'manicure_finish',
    'peel_preparation',
    'dental_exam',
    'hair_wash',
];
/** Exact target enums, never a family wildcard or an embedding admission rule. */
export const stockSupportingStepRules = {
    hair_wash: {
        treatments: ['haircut'],
        shotRole: 'supporting',
        regions: ['hair'],
        excludedName: 'dry.?cut|no.?wash|without.?wash|extensions?|colou?r|perm|relax|keratin|straighten',
        requiredName: 'cut|styling',
    },
    brow_grooming: {
        treatments: ['brow_lamination', 'brow_tint', 'powder_brows'],
        shotRole: 'supporting',
        regions: ['brow'],
        excludedName: 'inject|anti.?wrinkle|botox|botulinum|surg',
        requiredName: 'brow',
    },
    gentle_massage: {
        treatments: ['massage', 'swedish_massage'],
        shotRole: 'supporting',
        regions: ['neck', 'chest'],
        excludedName: 'deep.?tissue|pregnan|reflexolog|sports|lymph',
        requiredName: 'massage',
    },
    manicure_finish: {
        treatments: ['manicure', 'gel_nails'],
        shotRole: 'result',
        regions: ['hands'],
        excludedName: 'remov|overlay|french|toe|pedicure|extension|acrylic',
        requiredName: 'manicure|gel|shellac',
    },
    peel_preparation: {
        treatments: ['chemical_peel'],
        shotRole: 'preparation',
        regions: [],
        excludedName: '',
        requiredName: '',
    },
    dental_exam: {
        treatments: ['dentistry'],
        shotRole: 'supporting',
        regions: ['teeth'],
        excludedName: 'root.?canal|denture|aligner|whiten',
        requiredName: '\\mexam\\M|hygienist',
    },
};
/** Stock Footage + V2 Videos. Owner: Pavit Gogia. Expiry: 2026-12-31.
 * Cleanup: https://github.com/Borradh-Media/borradh-workspace/issues/1705. Default OFF. */
export const STOCK_FOOTAGE_MATCHING_NEW_FLAG = 'rollout-stock-footage-matching-new';
/** Paid formats remain available when the organic catalogue moves to V2. */
export const PAID_VIDEO_TEMPLATE_IDS = [
    'authority',
    'educational',
    'offer',
];
const PAID_VIDEO_VARIATION_IDS = [
    'authority-1',
    'authority-2',
    'authority-3',
    'educational-1',
    'educational-2',
    'educational-3',
    'offer-square-1',
];
/** Templates offered by the combined stock-footage and V2 rollout. */
export const V2_VIDEO_TEMPLATE_IDS = [
    'journey-1',
    'journey-2',
    'journey-3',
    'journey-4',
    'reel-2',
    'reel-3',
    'reel-4',
    'reset-me-time',
    'service-stock',
    'skin-edit',
    'step-by-step',
];
export function isV2VideoTemplate(id) {
    return V2_VIDEO_TEMPLATE_IDS.some((templateId) => templateId === id);
}
/** Paid formats keep their variations; only organic choices switch with V2. */
export function isVideoTemplateAvailable(id, v2Enabled) {
    return (PAID_VIDEO_TEMPLATE_IDS.some((templateId) => templateId === id) ||
        PAID_VIDEO_VARIATION_IDS.some((variationId) => variationId === id) ||
        isV2VideoTemplate(id) === v2Enabled);
}
//# sourceMappingURL=footage-spec.js.map